import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	MenuItem,
	Radio,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { CopySimpleIcon } from "@phosphor-icons/react/dist/ssr/CopySimple";

import type {
	CampaignRequestDetail,
	CloneableRequest,
	CloneEntry,
	CloneOmission,
} from "@/hooks/api/use-campaign-requests";
import {
	useCampaignRequestClonePreview,
	useCloneableCampaignRequests,
	useCloneCampaignRequestFrom,
	useCreateCampaignRequest,
} from "@/hooks/api/use-campaign-requests";

import { formatDate, formatDateRange, marketLabel } from "../constants";
import { StepHeading, WizardFooter } from "./wizard-shell";

/**
 * Step 0 — start.
 *
 * Repeating a previous campaign is the default, and deliberately so: a client runs variations of
 * the same campaign for years, and if cloning is one click the wizard gets used, while if it is
 * eight steps every time it gets bypassed by email.
 *
 * The manifest under the picker says what carries over *and* what does not. The thing that makes
 * people distrust a clone is not knowing what came with it, and listing both halves removes the
 * need to go and check every step.
 *
 * This is also where the request starts existing. The draft is written before the client reaches
 * step 1, so every later step has something to save against and a refresh never loses a decision.
 */

type StartMode = "repeat" | "scratch";

/** What the picker is allowed to say about a previous campaign. It only "ran" if it was provisioned. */
function describeRun(source: CloneableRequest): string {
	const range = formatDateRange(source.ranFrom, source.ranTo);

	if (source.provisionedAt) return range === "—" ? "ran previously" : `ran ${range}`;
	return `configured ${formatDate(source.createdAt)} · never ran`;
}

/**
 * How each line of the manifest is counted on its chip.
 *
 * The API writes every item in the plural because it is naming a set, so a source with one agent
 * put "1 agents" on the first thing a client ever reads in this wizard. Two of the lines are not
 * countable at all — "1 compliance settings" is worse than saying nothing — and those carry no
 * number. Anything not listed here is a line we have not written copy for, and it is printed as
 * the API sent it rather than pluralised by guesswork.
 */
const CLONE_ITEMS: Record<string, { one: string; many: string | null }> = {
	markets: { one: "market", many: "markets" },
	agents: { one: "agent", many: "agents" },
	// The chip says what it is; the qualifier — per market, without the dial dates — is helper
	// text under the row, because a chip carrying a subordinate clause is a sentence in a pill.
	"calling windows and retry rules": { one: "calling window", many: "calling windows" },
	outcomes: { one: "outcome", many: "outcomes" },
	"capture fields": { one: "capture field", many: "capture fields" },
	"notification email": { one: "notification email", many: null },
	"compliance settings": { one: "compliance settings", many: null },
};

/** A few words and a number. Everything else about the line goes underneath it. */
function cloneChipLabel(entry: CloneEntry): string {
	const words = CLONE_ITEMS[entry.item];

	if (!words) return entry.count === 1 ? entry.item : `${entry.count} ${entry.item}`;
	if (words.many === null) return words.one;

	return `${entry.count} ${entry.count === 1 ? words.one : words.many}`;
}

/**
 * One entry of the manifest: "3 agents (Poly EN, Poly BM, Poly TH)".
 *
 * The count and the names used to be two separate things on screen — six bright chips, then four
 * grey lines underneath naming the same four sets again, so "markets" was printed twice within an
 * inch of itself. One line, read out the way the canvas states it.
 */
function cloneEntryLine(entry: CloneEntry): string {
	const label = cloneChipLabel(entry);
	if (!entry.detail) return label;

	// Markets arrive as the codes they are stored under. "SG, AU" is our column, not their
	// campaign, so the same list is read out in the names they chose on step 1.
	const detail =
		entry.item === "markets"
			? entry.detail
					.split(",")
					.map((code) => marketLabel(code.trim()))
					.join(", ")
			: entry.detail;

	return `${label} (${detail})`;
}

/**
 * The manifest, as dot points — one thing per line.
 *
 * Both halves used to be single paragraphs strung together with middots, which is the shape of a
 * sentence rather than of a list: seven items in one run take a second read to count, and the
 * first thing a client wants from this panel is to count them.
 */
function CloneList({
	lines,
	muted = false,
}: {
	lines: { key: string; text: string }[];
	muted?: boolean;
}): React.JSX.Element {
	return (
		<Box component="ul" sx={{ listStyleType: "disc", pl: 2.5, m: 0 }}>
			{lines.map((line) => (
				<Typography
					key={line.key}
					component="li"
					variant="body2"
					color={muted ? "text.secondary" : "text.primary"}
					sx={{ display: "list-item" }}
				>
					{line.text}
				</Typography>
			))}
		</Box>
	);
}

/** Our shorthand, spelled out. The client never raised a "SOW". */
const OMISSION_LABELS: Record<string, string> = {
	"SOW and PO": "statement of work and purchase order",
};

function omissionLabel(entry: CloneOmission): string {
	return OMISSION_LABELS[entry.item] ?? entry.item;
}

export interface StepStartProps {
	/** Null until the draft has been written, which is what this step does. */
	request: CampaignRequestDetail | null;
	/**
	 * The draft now exists. Called with its id the instant the row is created, before anything
	 * else is attempted against it, so the address bar owns the draft from that moment on.
	 */
	onDraftCreated: (requestId: string) => void;
	onStarted: (requestId: string) => void;
	exit: () => void;
	/** A confirmed request takes no more content — the clone endpoint answers 422, not a copy. */
	readOnly?: boolean;
}

export function StepStart({ request, onDraftCreated, onStarted, exit, readOnly }: StepStartProps): React.JSX.Element {
	const [mode, setMode] = React.useState<StartMode>("repeat");
	const [sourceId, setSourceId] = React.useState<string>("");
	const [busy, setBusy] = React.useState(false);
	const [copyFailed, setCopyFailed] = React.useState(false);

	/**
	 * The draft this step wrote, held here as well as in the URL.
	 *
	 * The URL is the record; this is what the retry that happens in the same breath reads,
	 * because the router navigation and the refetch behind it have not landed by the time the
	 * client presses the button again.
	 */
	const draftId = React.useRef<string | null>(null);
	/** Whether the repeat this step promised has actually been written into that draft. */
	const copied = React.useRef(false);

	const { data: cloneable, isLoading: cloneableLoading } = useCloneableCampaignRequests();
	const { data: preview, isLoading: previewLoading } = useCampaignRequestClonePreview(
		mode === "repeat" && sourceId ? sourceId : null
	);

	const createMutation = useCreateCampaignRequest();
	const cloneMutation = useCloneCampaignRequestFrom();

	const options = React.useMemo(() => cloneable ?? [], [cloneable]);
	const hasPrevious = options.length > 0;

	// The most-used path is repeating the most recent campaign, so it is chosen rather than offered.
	React.useEffect(() => {
		if (!sourceId && options.length > 0) setSourceId(options[0].id);
	}, [options, sourceId]);

	// Nothing to repeat means the choice is not a choice; fall to a fresh start rather than
	// leaving the default selected against an empty picker.
	React.useEffect(() => {
		if (!cloneableLoading && !hasPrevious) setMode("scratch");
	}, [cloneableLoading, hasPrevious]);

	const source = options.find((option) => option.id === sourceId) ?? null;
	const alreadyStarted = Boolean(request);
	const canCopyInto = alreadyStarted && !readOnly;
	const carriesContent = Boolean(request && (request.agents.length > 0 || request.marketSchedules.length > 0));

	/**
	 * Writes the draft if there is not one yet, then repeats a campaign into it if asked to.
	 *
	 * Two writes, and the first one succeeding is a fact about the world whatever the second one
	 * does. It used to be kept in a local: the id went into a variable, the clone failed, the
	 * catch returned null, and the id was dropped on the floor — leaving a draft row nobody could
	 * reach, on a list where drafts are first-class rows the client can see. Pressing Continue
	 * again wrote another one. Ten attempts at a source we could not copy left ten of them.
	 *
	 * So the id is handed up the moment it exists, which puts it in the URL, and a retry finds it
	 * there and repeats into the draft it already has. The clone endpoint skips anything the
	 * target already carries, so retrying it is safe — a second attempt fills in what the first
	 * one did not manage rather than doubling what it did.
	 */
	const persist = async (): Promise<string | null> => {
		setBusy(true);
		setCopyFailed(false);

		try {
			let requestId = request?.id ?? draftId.current;

			if (!requestId) {
				const title = mode === "repeat" && source ? source.title : "Untitled campaign";
				const created = await createMutation.mutateAsync({ title });

				requestId = created.id;
				draftId.current = created.id;
				// Before the clone, deliberately. Everything after this line can fail without the
				// draft becoming unreachable.
				onDraftCreated(created.id);
			}

			// Cloning is part of starting, not a second decision: a fresh draft carries the source
			// across before the client ever sees step 1. Only for a draft this step wrote — repeating
			// into a request that already existed is `handleCopyIntoExisting`, on its own button.
			const owed = mode === "repeat" && Boolean(sourceId) && draftId.current !== null && !copied.current;

			if (owed) {
				await cloneMutation.mutateAsync({ requestId, sourceId });
				copied.current = true;
			}

			return requestId;
		} catch {
			// Both mutations name the API's own refusal in a toast. Staying on the step lets the
			// client pick a different source or try again — against the draft that now exists, not
			// against a new one.
			setCopyFailed(draftId.current !== null && !copied.current);
			return null;
		} finally {
			setBusy(false);
		}
	};

	const handleContinue = async (): Promise<void> => {
		const requestId = await persist();
		if (requestId) onStarted(requestId);
	};

	const handleSaveExit = async (): Promise<void> => {
		const requestId = await persist();
		if (requestId) exit();
	};

	/**
	 * Repeating into a request that already exists is an explicit act, never a side effect of
	 * pressing Continue — the clone appends, so doing it twice would silently double the agents.
	 */
	const handleCopyIntoExisting = async (): Promise<void> => {
		if (!request || !sourceId) return;

		setBusy(true);
		try {
			await cloneMutation.mutateAsync({ requestId: request.id, sourceId });
			// So Continue does not then repeat the same source a second time in the same sitting.
			copied.current = true;
			setCopyFailed(false);
		} catch {
			// Reported by the hook.
		} finally {
			setBusy(false);
		}
	};

	return (
		<Stack spacing={3}>
			<StepHeading title="What would you like to run?" help="New, or repeat what worked." />

			{copyFailed ? (
				<Alert severity="warning">
					Your draft was saved, but the campaign you asked to repeat could not be copied into it. Try again, or pick a
					different one.
				</Alert>
			) : alreadyStarted ? (
				<Alert severity="info">
					This request is already saved as a draft{request?.title ? ` — ${request.title}` : ""}. Continue where you left
					off, or copy a previous campaign into it below.
				</Alert>
			) : null}

			<Stack spacing={2}>
				<Card
					variant="outlined"
					sx={{
						borderColor: mode === "repeat" ? "primary.main" : "divider",
						cursor: hasPrevious ? "pointer" : "default",
						opacity: hasPrevious ? 1 : 0.6,
						p: 2,
					}}
					onClick={() => {
						if (hasPrevious) setMode("repeat");
					}}
				>
					<Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
						<Radio checked={mode === "repeat"} disabled={!hasPrevious} size="small" sx={{ p: 0, mt: 0.25 }} />

						<Box sx={{ flex: 1 }}>
							<Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
								<Typography variant="subtitle1">Repeat a previous campaign</Typography>
								<Chip color="primary" label="Fastest" size="small" variant="soft" />
							</Stack>
							<Typography variant="body2" color="text.secondary">
								Copies agents, markets, schedule and compliance settings. You supply new dates and a new lead list.
							</Typography>

							{hasPrevious ? null : (
								<Typography variant="caption" color="text.secondary">
									Nothing to repeat yet — this will be your first campaign with us.
								</Typography>
							)}
						</Box>
					</Stack>
				</Card>

				<Card
					variant="outlined"
					sx={{ borderColor: mode === "scratch" ? "primary.main" : "divider", cursor: "pointer", p: 2 }}
					onClick={() => setMode("scratch")}
				>
					<Stack direction="row" spacing={1.5} sx={{ alignItems: "flex-start" }}>
						<Radio checked={mode === "scratch"} size="small" sx={{ p: 0, mt: 0.25 }} />

						<Box sx={{ flex: 1 }}>
							<Typography variant="subtitle1">Start from scratch</Typography>
							<Typography variant="body2" color="text.secondary">
								A new campaign with new agents and markets.
							</Typography>
						</Box>
					</Stack>
				</Card>
			</Stack>

			{mode === "repeat" && hasPrevious ? (
				<Stack spacing={2}>
					{/* No label above it. It sits directly under the chosen "Repeat a previous
					    campaign" card and every option is a campaign title and when it ran, so
					    "Which campaign would you like to repeat?" was the card's own words again. */}
					<TextField
						select
						fullWidth
						value={sourceId}
						onChange={(event) => setSourceId(event.target.value)}
						slotProps={{ select: { "aria-label": "Campaign to repeat" } }}
					>
						{options.map((option) => (
							<MenuItem key={option.id} value={option.id}>
								{option.title} — {describeRun(option)}
							</MenuItem>
						))}
					</TextField>

					<Card variant="outlined" sx={{ p: 2 }}>
						<Stack spacing={1.5}>
							<Typography variant="subtitle2">What gets copied</Typography>

							{previewLoading ? <CircularProgress size={18} /> : null}

							{preview ? (
								<Stack spacing={1.5}>
									{/* Two lists: what comes, what does not. The seven reasons behind "not copied"
									    used to sit behind a "Why these are left behind" disclosure — the list is
									    the answer, and the reasons were an essay nobody opened. */}
									<CloneList
										lines={preview.copied.map((entry) => ({ key: entry.item, text: cloneEntryLine(entry) }))}
									/>

									{preview.notCopied.length === 0 ? (
										<Typography variant="body2" color="text.secondary">
											Nothing is left behind.
										</Typography>
									) : (
										<Box>
											<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
												Not copied
											</Typography>
											<CloneList
												muted
												lines={preview.notCopied.map((entry) => ({ key: entry.item, text: omissionLabel(entry) }))}
											/>
										</Box>
									)}
								</Stack>
							) : null}

							{!previewLoading && !preview ? (
								<Typography variant="body2" color="text.secondary">
									Choose a campaign to see what it would bring across.
								</Typography>
							) : null}
						</Stack>
					</Card>

					{canCopyInto ? (
						<Box>
							<Button
								variant="outlined"
								startIcon={<CopySimpleIcon />}
								disabled={busy || !sourceId}
								onClick={handleCopyIntoExisting}
							>
								Copy into this request
							</Button>
							{carriesContent ? (
								<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
									Copying again adds to the agents and markets already here.
								</Typography>
							) : null}
						</Box>
					) : null}
				</Stack>
			) : null}

			<WizardFooter
				busy={busy}
				continueDisabled={mode === "repeat" && hasPrevious && !sourceId}
				secondaryLabel="Cancel"
				onSecondary={exit}
				onSaveExit={() => void handleSaveExit()}
				onContinue={() => void handleContinue()}
			/>
		</Stack>
	);
}
